import { utcDay } from './gdacsTime.js';

export const FADE_FLOOR = 0.75;   // untimed / far days: the path stays clearly readable on any background
export const FADE_NEAR = 0.85;    // adjacent UTC day
export const CASING_ALPHA = 0.95; // the white track casing is persistent and never fades
export const RADIUS = { past: 3.5, forecast: 9, latest: 11 };
// Wind areas read as a bold, stacked shadow: every timed set is a plain translucent fill (darker for stronger wind),
// the set the playhead is on is stronger, and overlapping sets accumulate. Edges stay crisp on every raster.
export const WIND = {
  idleFill: { 60: 0.24, 90: 0.32, 120: 0.42 },
  activeFill: { 60: 0.36, 90: 0.48, 120: 0.60 },
  activeStroke: 0.95,
  idleStroke: 0.8,
  activeWeight: 2,
  idleWeight: 1.5,
};

// 1 on the playhead's UTC day, 0.8 one day away, 0.6 otherwise. Untimed -> FADE_FLOOR.
export function dayFade(ms, clockMs) {
  if (ms == null || clockMs == null) return FADE_FLOOR;
  const d = Math.abs(utcDay(ms) - utcDay(clockMs));
  return d === 0 ? 1 : d === 1 ? FADE_NEAR : FADE_FLOOR;
}

// The segment the playhead is on is always full strength; otherwise it follows the day of its END vertex.
export function segmentOpacity({ fromMs, toMs }, clockMs) {
  if (fromMs != null && toMs != null && fromMs <= clockMs && clockMs <= toMs) return 1;
  return dayFade(toMs ?? fromMs, clockMs);
}

// Index of the last timed forecast vertex at or before clockMs (the first forecast vertex when none; -1 without any).
export function latestVertexIndex(vertices, clockMs) {
  let first = -1;
  let latest = -1;
  vertices.forEach((v, i) => {
    if (v.phase !== 'forecast' || v.timeMs == null) return;
    if (first < 0) first = i;
    if (v.timeMs <= clockMs) latest = i;
  });
  return latest >= 0 ? latest : first;
}

// -> { segments: [{ opacity }], vertices: [{ opacity, radius }], wind: [{ active, fill: { 60, 90, 120 }, opacity, weight }] }
export function playStyles(parsed, clockState, clockMs) {
  const live = !!clockState && clockState.phase !== 'static' && clockMs != null;
  const V = parsed.track.vertices;
  const latest = live ? latestVertexIndex(V, clockMs) : -1;
  const segments = parsed.track.segments.map((_, i) => ({
    opacity: live ? segmentOpacity({ fromMs: V[i]?.timeMs ?? null, toMs: V[i + 1]?.timeMs ?? null }, clockMs) : 1,
  }));
  const vertices = V.map((v, i) => ({
    opacity: live ? dayFade(v.timeMs, clockMs) : 1,
    radius: v.phase === 'past' ? RADIUS.past : (i === latest ? RADIUS.latest : RADIUS.forecast),
  }));
  const activeIdx = live ? clockState.windIndex : parsed.currentWindIndex;
  const wind = parsed.windByTime.map((w, idx) => {
    const active = idx === activeIdx;
    return {
      active,
      fill: { ...(active ? WIND.activeFill : WIND.idleFill) },
      opacity: active ? WIND.activeStroke : WIND.idleStroke,
      weight: active ? WIND.activeWeight : WIND.idleWeight,
    };
  });
  return { segments, vertices, wind };
}
